/**
 * Supported document file types for upload and autoconversion.
 *
 * These types match the capabilities of the file converter service.
 * All files are automatically converted to PDF for storage.
 */

/**
 * Supported file extensions for document uploads
 */
export const SUPPORTED_FILE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.rtf'
] as const

/**
 * MIME types corresponding to supported file types
 * Maps to the file extensions above
 */
export const SUPPORTED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/pdf', // .pdf
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/plain', // .txt
  'application/rtf', // .rtf
  'application/x-rtf' // alternative rtf mime type
] as const

/**
 * String for HTML accept attribute
 * Can be used directly in file input elements
 */
export const FILE_INPUT_ACCEPT = '.jpg,.jpeg,.png,.heic,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf'

/**
 * Human-readable list of supported file types for display
 */
export const SUPPORTED_FILE_TYPES_DISPLAY = [
  'JPG',
  'JPEG',
  'PNG',
  'HEIC',
  'PDF',
  'DOC',
  'DOCX',
  'XLS',
  'XLSX',
  'TXT',
  'RTF'
] as const

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const fileName = filename.toLowerCase()
  if (!fileName.includes('.')) return ''
  return '.' + fileName.split('.').pop()
}

/**
 * Check if a file extension is supported
 */
export function isSupportedExtension(filename: string): boolean {
  const ext = getFileExtension(filename)
  return ext ? SUPPORTED_FILE_EXTENSIONS.includes(ext as any) : false
}

/**
 * Check if a MIME type is supported
 */
export function isSupportedMimeType(mimeType: string): boolean {
  return SUPPORTED_MIME_TYPES.includes(mimeType as any)
}

/**
 * Validate if a file is supported based on both MIME type and extension
 * This is more reliable than checking just one, as MIME types can be unreliable
 */
export function isFileSupported(file: File): boolean {
  const isValidMimeType = isSupportedMimeType(file.type)
  const isValidExtension = isSupportedExtension(file.name)
  return isValidMimeType || isValidExtension
}
